'use client'

import { useEffect } from 'react'

import * as CookieConsent from 'vanilla-cookieconsent'

import 'vanilla-cookieconsent/dist/cookieconsent.css'

import { sendGA4Event } from '~/components/CookieConsent/CookieConsentUtils'

export enum CookieCategory {
  Technical = 'technical',
  Statistical = 'statistical',
  Social = 'social',
  Marketing = 'marketing',
}

const sendClickEvent = async (param: { cookie: CookieConsent.CookieValue }) => {
  const { cookie } = param

  if (cookie.categories.length === 1 && cookie.categories.includes('technical')) {
    await sendGA4Event('click_reject')
  } else {
    await sendGA4Event('click_accept')
  }
}

const sendConsentAcceptanceEvent = async (param: { modalName: CookieConsent.ModalName }) => {
  if (param.modalName === 'preferencesModal') {
    await sendGA4Event('click_settings')
  }
}

const updateCookieConsent = () => CookieConsent.showPreferences()
const resetCookieConsent = () => CookieConsent.reset(true)

export const listenForConsent = () => {
  if (window._ccRun) return

  window.dataLayer = window.dataLayer || []

  function pushEventToGtm(event = 'consent', action = 'update', data?: { [key: string]: string }) {
    window.dataLayer?.push({ event, action, data })
  }

  pushEventToGtm('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  })

  const updateGtagConsent = () => {
    pushEventToGtm('consent', 'update', {
      ad_storage: CookieConsent.acceptedCategory(CookieCategory.Marketing) ? 'granted' : 'denied',
      ad_user_data: CookieConsent.acceptedCategory(CookieCategory.Marketing) ? 'granted' : 'denied',
      ad_personalization: CookieConsent.acceptedCategory(CookieCategory.Marketing) ? 'granted' : 'denied',
      analytics_storage: CookieConsent.acceptedCategory(CookieCategory.Statistical) ? 'granted' : 'denied',
      functionality_storage: CookieConsent.acceptedCategory(CookieCategory.Technical) ? 'granted' : 'denied',
      personalization_storage: CookieConsent.acceptedCategory(CookieCategory.Social) ? 'granted' : 'denied',
      security_storage: 'granted',
    })
  }

  window.addEventListener('cc:onConsent', () => {
    updateGtagConsent()
  })

  window.addEventListener('cc:onChange', () => {
    updateGtagConsent()
  })

  return () => {
    window.removeEventListener('cc:onConsent', updateGtagConsent)
    window.removeEventListener('cc:onChange', updateGtagConsent)
  }
}

const CookieConsentComponent = () => {
  useEffect(() => {
    /**
     * All config. options available here:
     * https://cookieconsent.orestbida.com/reference/configuration-reference.html
     */
    listenForConsent()
    CookieConsent.run({
      disablePageInteraction: true,
      autoShow: true,
      onConsent: sendClickEvent,
      onModalShow: sendConsentAcceptanceEvent,
      guiOptions: {
        consentModal: {
          layout: 'bar',
          position: 'bottom center',
          equalWeightButtons: false,
          flipButtons: true,
        },
      },
      categories: {
        technical: {
          enabled: true,
          readOnly: true,
        },
        statistical: {},
        social: {},
        marketing: {},
      },
      language: {
        default: 'en',
        autoDetect: 'document',
        translations: {
          en: '/lp/assets/cookies/en.json',
          cs: '/lp/assets/cookies/cs.json',
          de: '/lp/assets/cookies/de.json',
          pl: '/lp/assets/cookies/pl.json',
        },
      },
    })
  }, [])

  return <></>
}

export { CookieConsentComponent, resetCookieConsent, updateCookieConsent }
